import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Badge, Dropdown } from 'antd';
import {
  CaretUpFilled,
  ExclamationCircleOutlined,
  SoundFilled
} from '@ant-design/icons';
import { Spin } from 'antd';
import { formatTimeDifference } from '../../../../util/timezone';
import {
  NotificationMenuWrapper,
  StyledBellIcon,
  StyledMenu,
  StyledMenuItem,
  TriggerButtonContainer,
  BellIconWrapper,
  CaretContainer,
  NotificationHeader,
  NotificationTimestamp,
  NotificationContent,
  NotificationStatusMessage,
  StatusIcon
} from './styled';
import {
  navigateWithRefresh,
  parseNotificationAdditionalProps
} from './notificationNavigation';
import { resolveNotificationTarget } from './resolveNotificationTarget';

const NotificationsDropdown = ({
  history,
  getAMLSCommonNotifications,
  notifications = [],
  notificationsApiStatus = false,
  unreadCount = 0,
  moduleId
}) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [hasNewNotifications, setHasNewNotifications] = useState(true);

  const handleMenuClick = (e) => {
    setDropdownVisible(true);
  };

  useEffect(() => {
    if (dropdownVisible) getAMLSCommonNotifications();
  }, [dropdownVisible]);

  const triggerButton = (
    <TriggerButtonContainer
      data-testid="notifications-bell"
      onClick={() => setDropdownVisible(!dropdownVisible)}
    >
      <BellIconWrapper>
        {notifications?.length ? (
          <Badge
            dot={hasNewNotifications}
            offset={[-12, 4]}
            style={{
              backgroundColor: hasNewNotifications ? '#FF7B7B' : 'transparent',
              boxShadow: 'none'
            }}
          >
            <StyledBellIcon />
          </Badge>
        ) : (
          <StyledBellIcon />
        )}
      </BellIconWrapper>
      {dropdownVisible && (
        <CaretContainer id="topbar-id">
          <CaretUpFilled
            style={{
              fontSize: '20px',
              color: 'white',
              marginTop: '2px'
            }}
          />
        </CaretContainer>
      )}
    </TriggerButtonContainer>
  );

  const handleClickNotification = (notification) => {
    setDropdownVisible(false);

    // Check for scan completed notification (special case)
    if (notification.text.toLowerCase().includes('scan completed')) {
      const additionalProps = parseNotificationAdditionalProps(notification);
      history.push({
        pathname: `/app/smart-screening/on-demand-screening`,
        search: '?tabId=4',
        state: {
          jobId: additionalProps?.jobId
        }
      });
      return;
    }

    // FIN-13571 — the route is resolved once, shared with the SSE toast (NotificationListener).
    const target = resolveNotificationTarget(
      parseNotificationAdditionalProps(notification)
    );
    if (target) navigateWithRefresh({ history, ...target });
  };

  return (
    <Dropdown
      id="topbar-dropdown"
      overlay={
        <NotificationMenuWrapper>
          <StyledMenu onClick={handleMenuClick}>
            <StyledMenuItem
              key="1"
              style={{
                height: '48px',
                borderBottom: '1px solid #f7f7f8',
                margin: '0px'
              }}
            >
              <NotificationHeader>
                <span>Notifications</span>
                {notificationsApiStatus ? (
                  <Spin size="small" />
                ) : (
                  <StatusIcon>
                    <SoundFilled />
                  </StatusIcon>
                )}
              </NotificationHeader>
            </StyledMenuItem>
            {notifications && notifications?.length ? (
              notifications?.map((i, index) => {
                return (
                  <StyledMenuItem
                    key={index + 2}
                    onClick={() => handleClickNotification(i)}
                    style={{
                      height: 'auto',
                      minHeight: '56px',
                      borderBottom: '1px solid #f7f7f8',
                      margin: '0px',
                      padding: '8px 12px',
                      cursor: 'pointer'
                    }}
                  >
                    <NotificationContent>
                      <span className="bullet-text">&#8226;</span>
                      <span className="notification-text">{i.text}</span>
                    </NotificationContent>
                    <NotificationTimestamp>
                      {formatTimeDifference(i.createdAt)}
                    </NotificationTimestamp>
                  </StyledMenuItem>
                );
              })
            ) : (
              <>
                {!notificationsApiStatus && (
                  <StyledMenuItem
                    key="2"
                    style={{
                      height: '56px',
                      borderBottom: '1px solid #f7f7f8',
                      justifyContent: 'space-around',
                      margin: '0px'
                    }}
                  >
                    <NotificationStatusMessage>
                      No records found
                      <ExclamationCircleOutlined />
                    </NotificationStatusMessage>
                  </StyledMenuItem>
                )}
              </>
            )}
          </StyledMenu>
        </NotificationMenuWrapper>
      }
      trigger={['click']}
      visible={dropdownVisible}
      onVisibleChange={(visible) => setDropdownVisible(visible)}
      placement="bottomLeft"
    >
      {triggerButton}
    </Dropdown>
  );
};

NotificationsDropdown.propTypes = {
  getAMLSCommonNotifications: PropTypes.func,
  notifications: PropTypes.array,
  notificationsApiStatus: PropTypes.bool,
  unreadCount: PropTypes.number,
  history: PropTypes.object,
  moduleId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default NotificationsDropdown;
